/**
 * In-memory store of recent events for the dev widget. Bounded ring buffer + counters.
 * Decoupled from transport: every captured event is recorded here AND queued for sending.
 */
import type { RequestEvent } from '@aris/request-watcher-shared';

export interface Counters {
  total: number;
  errors: number;
  unknownDomains: number;
  unplanned: number;
}

type Listener = () => void;

const MAX_RECENT = 100;

class Store {
  recent: RequestEvent[] = [];
  counters: Counters = { total: 0, errors: 0, unknownDomains: 0, unplanned: 0 };
  private unknownHosts = new Set<string>();
  private listeners = new Set<Listener>();

  record(ev: RequestEvent): void {
    this.recent.unshift(ev);
    if (this.recent.length > MAX_RECENT) this.recent.pop();

    this.counters.total += 1;
    if (ev.error || (ev.status !== null && ev.status >= 400)) this.counters.errors += 1;
    if (!ev.planned) this.counters.unplanned += 1;
    if (ev.direction === 'outgoing' && !ev.domain_known && ev.host) {
      this.unknownHosts.add(ev.host);
      this.counters.unknownDomains = this.unknownHosts.size;
    }
    this.emit();
  }

  unknownDomainList(): string[] {
    return [...this.unknownHosts];
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  exportJson(): string {
    return JSON.stringify({ counters: this.counters, events: this.recent }, null, 2);
  }

  /** Minimal HAR 1.2 export of recent events. */
  exportHar(): string {
    const har = {
      log: {
        version: '1.2',
        creator: { name: 'aris-request-watcher', version: '0.1.0' },
        entries: this.recent.map((ev) => ({
          startedDateTime: ev.timestamp,
          time: ev.duration_ms ?? 0,
          request: {
            method: ev.method,
            url: ev.url,
            headers: Object.entries(ev.request_headers ?? {}).map(([name, value]) => ({ name, value })),
            headersSize: -1,
            bodySize: ev.request_body_size ?? 0,
          },
          response: {
            status: ev.status ?? 0,
            statusText: '',
            headers: Object.entries(ev.response_headers ?? {}).map(([name, value]) => ({ name, value })),
            headersSize: -1,
            bodySize: ev.response_body_size ?? 0,
          },
          _aris: { source: ev.source, cause: ev.cause, planned: ev.planned, trace_id: ev.trace_id },
        })),
      },
    };
    return JSON.stringify(har, null, 2);
  }
}

export const store = new Store();
